import React, { useEffect, useMemo, useRef, useState } from "react";
import ComponentCustomModal from "../../../components/common/customModal/ComponentCustomModal";
import { FieldArray, Form, Formik } from "formik";
import { Tab, Tabs } from "react-bootstrap";
import { FtextField } from "../../../components/common/FtextField";
import {
  CashStack,
  DashCircle,
  PenFill,
  PlusCircle,
  ThreeDots,
  Printer,
} from "react-bootstrap-icons";

import Loader from "../../../components/common/Loader";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  bandwidthSaleInvoice,
  getBandwidthInvoiceByReseller,
  updateBandwidthSaleInvoice,
} from "../../../features/apiCallReseller";
import useISPowner from "../../../hooks/useISPOwner";
import Table from "../../../components/table/Table";
import { badge } from "../../../components/common/Utils";
import ResellerRechargeDialog from "./ResellerRechargeDialog";
import * as Yup from "yup";
import { useReactToPrint } from "react-to-print";
import BandwidthInvoice from "./BandwidthInvoice";

const monthValues = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const ResellerInvoice = ({ show, setShow, reseller }) => {
  const validationSchema = Yup.object({
    bandwidth: Yup.array().of(
      Yup.object({
        startDate: Yup.date().required("Start date is required"),
        endDate: Yup.date().required("End date is required"),
      }),
    ),
  });

  const componentRef = useRef(null);
  let today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  const { t } = useTranslation();
  const dispatch = useDispatch();

  //---> Get user & current user data form useISPOwner hooks
  const { ispOwnerId, userData } = useISPowner();

  //---> Get ispOwner bandwidth sale invoices form redux store
  const invoices = useSelector((state) => state?.reseller?.bandwidthInvoice);

  //---> Get ispOwner bandwidth packages form redux store
  const packages = useSelector((state) => state?.reseller?.bandwidthPackage);

  //---> Local State
  const [loading, setLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [invoiceTabs, setInvoiceTabs] = useState("invoice");
  const [modalStatus, setModalStatus] = useState("");
  const [newShow, setNewShow] = useState(false);
  const [invoice, setInvoice] = useState({});
  const [isUpdate, setIsUpdate] = useState(false);
  const [invoieData, setInvoiceData] = useState();
  const [defaultValues, setDefaultValue] = useState({
    month: today,
    bandwidth: [
      {
        package: "",
        startDate: firstDayOfMonth,
        endDate: new Date(),
        quantity: 0,
        price: 0,
        total: 0,
      },
    ],
    totalAmount: 0,
    previousDue: reseller?.due,
    grandTotal: 0,
  });

  // Print Hander Function -------------------
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: "invoice",
  });

  useEffect(() => {
    setInvoiceTabs("invoice");
    getBandwidthInvoiceByReseller(dispatch, reseller?._id, setIsLoading);
  }, [reseller]);

  useEffect(() => {
    if (isUpdate && invoice) {
      setDefaultValue((prev) => ({
        ...prev,
        ...invoice,
        month: new Date(2025, monthValues.indexOf(invoice.month), 1),
      }));
    } else if (reseller) {
      setInvoiceData(invoices);
    }
  }, [isUpdate, invoice, invoieData]);

  // multiple tabs control
  const changeTab = (key) => {
    setInvoiceTabs(key);
  };

  const formValueHandlerFunction = (
    { target: { value, name } },
    index,
    values,
    setFieldValue,
  ) => {
    let { quantity, price, startDate, endDate } = values.bandwidth[index] || {};
    let { grandTotal, dueAmount } = values;

    if (name === `bandwidth.${index}.package`) {
      const bandwidth = packages?.find((item) => item._id === value);

      setFieldValue(`bandwidth.${index}.price`, bandwidth ? bandwidth.rate : 0);
    }

    if (name === `bandwidth.${index}.endDate`) {
      const date = new Date(value);
      const start = new Date(startDate);

      const year = date.getFullYear();
      const month = date.getMonth();

      const startDayOfMonth = start.getDate();
      const endDayOfMonth = date.getDate();

      const daysInMonth = new Date(year, month + 1, 0).getDate();

      // inclusive day count
      const dayOfMonth = endDayOfMonth - startDayOfMonth + 1;

      const total = Math.round(((quantity * price) / daysInMonth) * dayOfMonth);
      values.bandwidth[index].total = Number(total);
      setFieldValue(`bandwidth.${index}.total`, total);
    }

    if (name === `bandwidth.${index}.quantity`) {
      const start = new Date(startDate);
      const end = new Date(endDate);

      const year = end.getFullYear();
      const month = end.getMonth();

      const startDayOfMonth = start.getDate();
      const endDayOfMonth = end.getDate();

      const daysInMonth = new Date(year, month + 1, 0).getDate();

      // inclusive day count
      const dayOfMonth = endDayOfMonth - startDayOfMonth + 1;

      const total = Math.round(
        ((Number(value) * price) / daysInMonth) * dayOfMonth,
      );

      values.bandwidth[index].quantity = Number(value);
      values.bandwidth[index].total = Number(total);
      setFieldValue(`bandwidth.${index}.total`, total);
    }

    if (name === `bandwidth.${index}.price`) {
      const start = new Date(startDate);
      const end = new Date(endDate);

      const year = end.getFullYear();
      const month = end.getMonth();

      const startDayOfMonth = start.getDate();
      const endDayOfMonth = end.getDate();

      const daysInMonth = new Date(year, month + 1, 0).getDate();

      // inclusive day count
      const dayOfMonth = endDayOfMonth - startDayOfMonth + 1;

      const total = Math.round(
        ((Number(value) * quantity) / daysInMonth) * dayOfMonth,
      );

      values.bandwidth[index].price = Number(value);
      values.bandwidth[index].total = Number(total);
      setFieldValue(`bandwidth.${index}.total`, total);
    }

    if (name === "previousDue") {
      values.previousDue = Number(value);
      setFieldValue("previousDue", Number(value));
    }

    let totalAmount = 0;
    let totalBill = 0;
    values.bandwidth.forEach((_, _index) => {
      const fieldTotal = values.bandwidth[_index].total || 0;

      totalAmount += fieldTotal;
      totalBill = totalAmount + values.previousDue;
    });

    setFieldValue("totalAmount", totalAmount);
    setFieldValue("grandTotal", totalBill);

    if (isUpdate) {
      const dueSum = grandTotal - totalBill;
      setFieldValue("dueAmount", dueAmount - dueSum);
    }
  };

  const handleBandwidthSaleInvoiceSubmit = (formValues) => {
    const sendData = {
      ...formValues,
      month: monthValues[new Date(formValues.month).getMonth()],
      dueAmount: isUpdate ? formValues.dueAmount : formValues.grandTotal,
      reseller: reseller?._id,
      ispOwner: ispOwnerId,
    };

    if (isUpdate) {
      updateBandwidthSaleInvoice(dispatch, invoice?._id, sendData, setLoading);
    } else {
      bandwidthSaleInvoice(dispatch, sendData, setIsLoading);
    }
  };

  // table column
  const columns = useMemo(
    () => [
      {
        width: "5%",
        Header: "#",
        id: "row",
        accessor: (row) => Number(row.id + 1),
        Cell: ({ row }) => <strong>{Number(row.id) + 1}</strong>,
      },
      {
        width: "10%",
        Header: t("month"),
        accessor: "month",
      },
      {
        width: "10%",
        Header: t("totalAmount"),
        accessor: "totalAmount",
      },
      {
        width: "10%",
        Header: t("previousDue"),
        accessor: "previousDue",
      },
      {
        width: "10%",
        Header: t("grandTotal"),
        accessor: "grandTotal",
      },
      {
        width: "10%",
        Header: t("due"),
        accessor: "dueAmount",
      },
      {
        width: "10%",
        Header: t("paymentStatus"),
        accessor: "paymentStatus",
        Cell: ({ cell: { value } }) => badge(value),
      },
      {
        width: "7%",
        Header: () => <div className="text-center">{t("action")}</div>,
        id: "option",
        Cell: ({ row: { original } }) => (
          <div className="d-flex justify-content-center align-items-center">
            <div className="dropdown">
              <ThreeDots
                className="dropdown-toggle ActionDots"
                id="areaDropdown"
                type="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              />
              <ul className="dropdown-menu" aria-labelledby="resellerDropdown">
                <li
                  onClick={() => {
                    setInvoiceTabs("createInvoice");
                    setInvoice(original);
                    setIsUpdate(true);
                  }}
                >
                  <div className="dropdown-item">
                    <div className="customerAction">
                      <PenFill />
                      <p className="actionP">{t("edit")}</p>
                    </div>
                  </div>
                </li>

                <li
                  onClick={() => {
                    setInvoiceData(original);
                    setTimeout(() => {
                      handlePrint();
                    }, 0);
                  }}
                >
                  <div className="dropdown-item">
                    <div className="customerAction">
                      <Printer />
                      <p className="actionP">{t("print")}</p>
                    </div>
                  </div>
                </li>

                <li
                  onClick={() => {
                    setInvoice(original);
                    setModalStatus("recharge");
                    setNewShow(true);
                  }}
                >
                  <div className="dropdown-item">
                    <div className="customerAction">
                      <CashStack />
                      <p className="actionP">{t("collection")}</p>
                    </div>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        ),
      },
    ],
    [t],
  );

  return (
    <>
      <ComponentCustomModal
        show={show}
        setShow={setShow}
        centered={false}
        size={"xl"}
        header={t("addNewBandwidth")}
      >
        <Tabs
          defaultActiveKey="invoice"
          id="uncontrolled-tab-example"
          activeKey={invoiceTabs}
          onSelect={(eventKey) => changeTab(eventKey)}
        >
          <Tab eventKey="invoice" title={t("invoice")}>
            <div className="collectorWrapper mt-2 py-1">
              <div className="table-section">
                <Table
                  isLoading={isLoading}
                  columns={columns}
                  data={invoices}
                />
              </div>
            </div>
          </Tab>

          <Tab eventKey="createInvoice" title={t("createInvoice")}>
            <Formik
              initialValues={defaultValues}
              validationSchema={validationSchema}
              onSubmit={(values) => {
                handleBandwidthSaleInvoiceSubmit(values);
              }}
              enableReinitialize
            >
              {({ values, setFieldValue }) => (
                <Form>
                  <div className="displayGrid mt-3">
                    <div className="displayGrid5 mb-2">
                      <FtextField
                        {...{
                          name: "month",
                          type: "date",
                          name: "month",
                          label: t("month"),
                          validation: true,
                          component: "DatePicker",
                          dateFormat: "MMM-yyyy",
                          showMonthYearPicker: true,
                          showFullMonthYearPicker: true,
                          minDate: new Date(userData?.createdAt),
                          maxDate: firstDayOfMonth,
                        }}
                      />
                    </div>

                    <p
                      className="clintTitle"
                      style={{
                        fontSize: "18px",
                        fontWeight: 500,
                        padding: "6px",
                      }}
                    >
                      {t("selectBandwidthPackage")}
                    </p>

                    <table className="table align-middle">
                      <thead className="text-center">
                        <tr>
                          <th style={{ width: "25%" }} className="border">
                            {t("bandwidth")}
                          </th>
                          <th style={{ width: "25%" }} className="border">
                            {t("dateToDate")}
                          </th>
                          <th style={{ width: "15%" }} className="border">
                            {t("quantity")} (Mbps)
                          </th>
                          <th style={{ width: "15%" }} className="border">
                            {t("price")}
                          </th>
                          <th style={{ width: "15%" }} className="border">
                            {t("total")}
                          </th>
                          <th style={{ width: "5%" }} className="border">
                            {t("action")}
                          </th>
                        </tr>
                      </thead>

                      <FieldArray name="bandwidth">
                        {({ push, remove }) => (
                          <tbody>
                            {values.bandwidth.map((field, index) => (
                              <tr key={index}>
                                <td className="border">
                                  <FtextField
                                    {...{
                                      name: `bandwidth.${index}.package`,
                                      as: "select",
                                      id: `bandwidth.${index}.package`,
                                      options: packages.map((item) => ({
                                        text: item.name,
                                        value: item._id,
                                      })),
                                      firstOptions: t("bandwidth"),
                                      textAccessor: "text",
                                      valueAccessor: "value",
                                      onChange: (event) =>
                                        formValueHandlerFunction(
                                          event,
                                          index,
                                          values,
                                          setFieldValue,
                                        ),
                                    }}
                                  />
                                </td>

                                <td>
                                  <div className="d-flex gap-2 align-items-center">
                                    <FtextField
                                      {...{
                                        name: `bandwidth.${index}.startDate`,
                                        type: "date",
                                        name: `bandwidth.${index}.startDate`,
                                        component: "DatePicker",
                                        dateFormat: "yyyy MM dd",
                                      }}
                                    />
                                    <strong>to</strong>
                                    <FtextField
                                      {...{
                                        name: `bandwidth.${index}.endDate`,
                                        type: "date",
                                        name: `bandwidth.${index}.endDate`,
                                        component: "DatePicker",
                                        dateFormat: "yyyy MM dd",
                                        onChange: (event) =>
                                          formValueHandlerFunction(
                                            event,
                                            index,
                                            values,
                                            setFieldValue,
                                          ),
                                      }}
                                    />
                                  </div>
                                </td>

                                <td className="border">
                                  <FtextField
                                    {...{
                                      name: `bandwidth.${index}.quantity`,
                                      type: "text",
                                      id: `bandwidth.${index}.quantity`,
                                      className: "form-control text-center",
                                      onChange: (event) =>
                                        formValueHandlerFunction(
                                          event,
                                          index,
                                          values,
                                          setFieldValue,
                                        ),
                                    }}
                                  />
                                </td>

                                <td className="border">
                                  <FtextField
                                    {...{
                                      name: `bandwidth.${index}.price`,
                                      type: "text",
                                      id: `bandwidth.${index}.price`,
                                      className: "form-control text-center",
                                      onChange: (event) =>
                                        formValueHandlerFunction(
                                          event,
                                          index,
                                          values,
                                          setFieldValue,
                                        ),
                                    }}
                                  />
                                </td>

                                <td className="border">
                                  <FtextField
                                    {...{
                                      name: `bandwidth.${index}.total`,
                                      type: "text",
                                      id: `bandwidth.${index}.total`,
                                      className: "form-control text-center",
                                    }}
                                  />
                                </td>

                                <td className="text-center border">
                                  {index + 1 === values.bandwidth.length ? (
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-primary"
                                      onClick={() =>
                                        push({
                                          package: "",
                                          startDate: firstDayOfMonth,
                                          endDate: new Date(),
                                          quantity: 0,
                                          price: 0,
                                          total: 0,
                                        })
                                      }
                                    >
                                      <PlusCircle size={20} />
                                    </button>
                                  ) : (
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-danger text-center"
                                      onClick={() => remove(index)}
                                    >
                                      <DashCircle size={20} />
                                    </button>
                                  )}
                                </td>
                              </tr>
                            ))}

                            <tr>
                              <td colSpan="3" className="border-0"></td>
                              <td
                                className="border"
                                style={{ fontWeight: 650 }}
                              >
                                {t("totalAmount")}
                              </td>
                              <td className="border">
                                <FtextField
                                  {...{
                                    name: "totalAmount",
                                    type: "text",
                                    id: "totalAmount",
                                  }}
                                />
                              </td>
                            </tr>

                            <tr>
                              <td colSpan="3" className="border-0"></td>
                              <td
                                className="border"
                                style={{ fontWeight: 550 }}
                              >
                                {t("previousDue")}
                              </td>
                              <td className="border">
                                <FtextField
                                  {...{
                                    name: "previousDue",
                                    type: "text",
                                    id: "previousDue",
                                    onChange: (event) =>
                                      formValueHandlerFunction(
                                        event,
                                        null,
                                        values,
                                        setFieldValue,
                                      ),
                                  }}
                                />
                              </td>
                            </tr>

                            <tr>
                              <td colSpan="3" className="border-0"></td>
                              <td
                                className="border"
                                style={{ fontWeight: 600 }}
                              >
                                {t("grandTotal")}
                              </td>
                              <td className="border">
                                <FtextField
                                  {...{
                                    name: "grandTotal",
                                    type: "text",
                                    id: "grandTotal",
                                  }}
                                />
                              </td>
                            </tr>

                            {isUpdate && (
                              <tr>
                                <td colSpan="3" className="border-0"></td>
                                <td
                                  className="border"
                                  style={{ fontWeight: 650 }}
                                >
                                  {t("dueAmount")}
                                </td>
                                <td className="border">
                                  <FtextField
                                    {...{
                                      name: "dueAmount",
                                      type: "text",
                                      id: "dueAmount",
                                    }}
                                  />
                                </td>
                              </tr>
                            )}
                          </tbody>
                        )}
                      </FieldArray>
                    </table>
                  </div>

                  <div className="displayGrid1 float-end mt-4">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      disabled={isLoading}
                      onClick={() => setShow(false)}
                    >
                      {t("cancel")}
                    </button>
                    <button
                      type="submit"
                      className="btn btn-success customBtn"
                      disabled={isLoading}
                    >
                      {isLoading ? <Loader /> : t("save")}
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          </Tab>
        </Tabs>
      </ComponentCustomModal>

      {/* Invoice paid dialog */}
      {modalStatus === "recharge" && (
        <ResellerRechargeDialog
          show={newShow}
          setShow={setNewShow}
          invoice={invoice}
        />
      )}

      {/* Invoice print and pdf */}
      <div style={{ display: "none" }}>
        <BandwidthInvoice invoieData={invoieData} ref={componentRef} />
      </div>
    </>
  );
};

export default ResellerInvoice;
